import React, { useState } from 'react';
import { X, Wallet } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../../hooks/use-toast';

const WalletAdjustmentModal = ({ isOpen, onClose, driver, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !driver) return null;

  const handleSubmit = async () => {
    const adjustmentAmount = parseFloat(amount);
    
    if (isNaN(adjustmentAmount) || adjustmentAmount === 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid non-zero amount',
        variant: 'destructive',
      });
      return;
    }

    if (!reason || reason.trim().length < 5) {
      toast({
        title: 'Reason Required',
        description: 'Please provide a reason (at least 5 characters)',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const api = (await import('../../utils/api')).default;
      
      const response = await api.put(`/admin/wallet/driver/${driver.userId}/adjust`, {
        amount: adjustmentAmount,
        reason: reason.trim(),
        notes: notes.trim() || null,
      });

      toast({
        title: 'Wallet Adjusted',
        description: `${adjustmentAmount > 0 ? 'Added' : 'Deducted'} K${Math.abs(adjustmentAmount).toFixed(2)}. New balance: K${response.data.newBalance.toFixed(2)}`,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adjusting wallet:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to adjust wallet',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
            <Wallet className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Adjust Wallet</h2>
            <p className="text-sm text-gray-600">{driver.name}</p>
          </div>
        </div>

        {/* Current Balance */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 mb-1">Current Balance</p>
          <p className="text-3xl font-bold text-gray-900">
            K{driver.walletBalance?.toFixed(2) || '0.00'}
          </p>
        </div>

        {/* Amount Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adjustment Amount *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              K
            </span>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Use positive number to add, negative to subtract
          </p>
        </div>

        {/* Reason */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason *
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          >
            <option value="">Select reason...</option>
            <option value="Wallet Top Up">Wallet Top Up</option>
            <option value="Bonus payment">Bonus payment</option>
            <option value="Driver compensation">Driver compensation</option>
            <option value="Refund for poor service">Refund for poor service</option>
            <option value="Balance correction">Balance correction</option>
            <option value="System error correction">System error correction</option>
            <option value="Penalty for violation">Penalty for violation</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional context..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
            rows={3}
            maxLength={500}
          />
        </div>

        {/* Preview */}
        {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) !== 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-blue-900 mb-1">Preview</p>
            <p className="text-sm text-blue-800">
              Current: K{driver.walletBalance?.toFixed(2) || '0.00'} →
              New: K{(driver.walletBalance + parseFloat(amount)).toFixed(2)}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-pink-600 hover:bg-pink-700"
            disabled={submitting || !amount || !reason}
          >
            {submitting ? 'Processing...' : 'Confirm Adjustment'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default WalletAdjustmentModal;
